import clsx from 'clsx'
import React from 'react'
import { useTranslation } from 'react-i18next'
import Select, {
  MultiValue,
  MultiValueProps,
  Props as SelectProps
} from 'react-select'

import { inputVariants } from '@/components-latest/primitives/Controls/Input'
import { Close } from '@/components-latest/primitives/Icons'
import Text from '@/components-latest/primitives/Text'

import Styles from './ChipSelect.module.scss'

interface Option {
  value: string
  label: string
}

interface ChipSelectProps extends Omit<SelectProps<Option, true>, 'onChange'> {
  onChange: (value: Option[]) => void
  value: Option[]
  name: string
  isLoading?: boolean
  emptyOptionsMessage?: string
}

type CustomMultiValueProps = MultiValueProps<Option, true>

const CustomMultiValue: React.FC<CustomMultiValueProps> = props => {
  const { children, removeProps, options } = props

  return (
    <div className={Styles.chip} {...removeProps}>
      <Text variant="nextBody">{children}</Text>
      <Close className={Styles.close} />
    </div>
  )
}

const ChipSelect = React.forwardRef<any, ChipSelectProps>(
  (
    {
      onChange,
      emptyOptionsMessage,
      value,
      name,
      options,
      placeholder,
      isSearchable = true,
      isClearable = false,
      isDisabled = false,
      closeMenuOnSelect = false,
      isLoading = true,
      ...rest
    },
    ref
  ) => {
    const { t } = useTranslation()

    const handleChange = (newValue: MultiValue<Option>) => {
      onChange(newValue as Option[])
    }

    return (
      <Select
        name={name}
        value={value}
        onChange={handleChange}
        options={isLoading ? [] : options}
        isMulti
        isClearable={isClearable}
        isDisabled={isDisabled}
        isSearchable={isSearchable}
        closeMenuOnSelect={closeMenuOnSelect}
        placeholder={placeholder}
        noOptionsMessage={() => t('registration.cuisine.no-options')}
        classNames={{
          dropdownIndicator: () => Styles.hide,
          valueContainer: () => clsx(inputVariants(), Styles.valueContainer),
          indicatorsContainer: () => Styles.hide,
          input: () => Styles.input,
          placeholder: () => Styles.placeholder,
          container: () => Styles.container,
          control: () => Styles.control,
          multiValueLabel: () => Styles.multiValueLabel
        }}
        components={{
          MultiValue: CustomMultiValue,
          NoOptionsMessage: () => (
            <div className={Styles.emptyMessage}>
              <Text variant="nextBody">{emptyOptionsMessage || ''}</Text>
            </div>
          )
        }}
        isLoading={isLoading}
        ref={ref}
        {...rest}
      />
    )
  }
)
ChipSelect.displayName = 'ChipSelect'

export default ChipSelect
